import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Page from 'Core/components/Page';
import Card from 'Core/components/FormElements/Card';
import { FaPhoneAlt } from 'react-icons/fa';

const PhoneCard = ({ title, phone, description }) => (
  <a
    href={`tel:${phone}`}
    className="block p-5 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 hover:shadow-md transition-all duration-200 group"
  >
    <div className="flex items-center gap-4">
      <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
        <FaPhoneAlt className="text-blue-600" />
      </div>
      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-blue-600 font-semibold">{phone}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  </a>
);

const InfoCard = ({
  icon: Icon,
  title,
  description,
  iconBgColor = 'bg-sky-50',
  iconColor = 'text-sky-600',
}) => (
  <div className="flex items-start gap-4 p-5 bg-slate-50 border border-slate-200 rounded-lg">
    {Icon && (
      <div
        className={`flex-shrink-0 w-12 h-12 ${iconBgColor} rounded-full flex items-center justify-center`}
      >
        <Icon className={`${iconColor} text-lg`} />
      </div>
    )}
    <div>
      {title && <p className="font-medium text-gray-900 mb-1">{title}</p>}
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  </div>
);

/**
 * Shared layout for Fortiva edge-case / exception screens.
 * Renders a branded page with a gradient header card, a body description,
 * an optional highlighted info card, and an optional support contact section.
 * Pass copy and theming via props so each case stays a thin wrapper.
 */
export default function FinancingExceptionPage({
  pageTitle,
  icon: Icon,
  title,
  subtitle,
  description,
  testId,
  gradientClass = 'from-emerald-500 to-teal-500',
  gradientStyle,
  subtitleColorClass = 'text-emerald-100',
  badgeClass = 'bg-emerald-100 text-emerald-700',
  infoCard,
  contact,
}) {
  const { t } = useTranslation();

  if (pageTitle) {
    document.title = pageTitle;
  }
  useScrollTop();

  return (
    <Page
      title={t('page.financing.header')}
      withStepProgress={false}
      stickyFooter
      branding="fortiva"
    >
      <div className="max-w-3xl mx-auto mt-8">
        <Card className="!p-0 overflow-hidden" cardPadding="none">
          {/* Header */}
          <div
            className={`${gradientStyle ? '' : `bg-gradient-to-r ${gradientClass}`} px-8 py-7`}
            style={gradientStyle ? { background: gradientStyle } : {}}
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                {Icon && <Icon className="text-white text-2xl" />}
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-white" data-testid={testId}>
                  {title}
                </h1>
                {subtitle && <p className={`${subtitleColorClass} text-base mt-1`}>{subtitle}</p>}
              </div>
            </div>
          </div>

          <div className="p-8 md:p-10">
            <div className="text-gray-600 text-lg leading-relaxed mb-6">{description}</div>

            {infoCard && <InfoCard {...infoCard} />}

            {contact && (
              <div className="space-y-3">
                {contact.instruction && (
                  <div className="flex items-center gap-2 mb-3">
                    {contact.icon && (
                      <span
                        className={`w-8 h-8 ${badgeClass} rounded-full flex items-center justify-center`}
                      >
                        <contact.icon />
                      </span>
                    )}
                    <span className="font-medium text-gray-700">{contact.instruction}</span>
                  </div>
                )}
                <PhoneCard
                  title={contact.title}
                  phone={contact.phone}
                  description={contact.description}
                />
              </div>
            )}
          </div>
        </Card>
      </div>
    </Page>
  );
}
